import { format } from 'date-fns';

import { APIConfiguration } from '@savgroup-front-common/configuration';
import { LANGUAGES } from '@savgroup-front-common/constants';
import {
  COMMON_QUERY_PARAMS,
  SUPPORTED_METHODS,
} from '@savgroup-front-common/constants/shared';
import {
  buildUrl,
  intl,
  prepareResponseFailure,
} from '@savgroup-front-common/core/helpers';
import { apiCall } from '@savgroup-front-common/core/services';
import {
  BackResponse,
  BackResponseFailure,
  OwnerSummary,
} from '@savgroup-front-common/types';

const ENDPOINT = `${APIConfiguration.owner}anonymous/orders/${COMMON_QUERY_PARAMS.REFERENCE}`;

interface GetOrderByOrderReferenceAndOrderPriceArgs {
  sellerId: string;
  totalPrice?: number | string;
  PhoneNumberLastFourDigits?: string;
  orderReference: string;
  purchaseDate?: Date;
  email?: string;
}

export enum ORDER_BY_REF_AND_PRICE_PAYLOAD_TYPE {
  ORDER_ALREADY_IMPORT = 'OrderAlreadyImport',
  ANONYMOUS_CUSTOMER = 'AnonymousCustomer',
  CUSTOMER_IDENTIFIED = 'CustomerIdentified',
}

export type GetOrderByOrderReferenceAndOrderPriceResponseCustomerIdentified = {
  orderReference: string;
  owner: OwnerSummary;
  importStatus: ORDER_BY_REF_AND_PRICE_PAYLOAD_TYPE.CUSTOMER_IDENTIFIED;
};

export type GetOrderByOrderReferenceAndOrderPriceResponseAnonymousCustomer = {
  orderReference: string;
  importStatus: ORDER_BY_REF_AND_PRICE_PAYLOAD_TYPE.ANONYMOUS_CUSTOMER;
};

export type GetOrderByOrderReferenceAndOrderPriceResponseOrderAlreadyImport = {
  irshUrl: Location;
  importStatus: ORDER_BY_REF_AND_PRICE_PAYLOAD_TYPE.ORDER_ALREADY_IMPORT;
};

export type GetOrderByOrderReferenceAndOrderPriceResponse =
  | GetOrderByOrderReferenceAndOrderPriceResponseCustomerIdentified
  | GetOrderByOrderReferenceAndOrderPriceResponseAnonymousCustomer
  | GetOrderByOrderReferenceAndOrderPriceResponseOrderAlreadyImport;

async function getOrderByOrderReferenceAndOrderPrice({
  sellerId,
  orderReference,
  totalPrice,
  PhoneNumberLastFourDigits,
  purchaseDate,
  email,
}: GetOrderByOrderReferenceAndOrderPriceArgs): Promise<
  | BackResponse<GetOrderByOrderReferenceAndOrderPriceResponse>
  | BackResponseFailure
> {
  const url = buildUrl(
    ENDPOINT.replace(COMMON_QUERY_PARAMS.REFERENCE, orderReference),
    {
      sellerId,
      orderReference,
      totalPrice,
      PhoneNumberLastFourDigits,
      purchaseDate: purchaseDate
        ? format(purchaseDate, 'yyyy-MM-dd')
        : undefined,
      email,
      preferredLanguage: intl?.locale ?? LANGUAGES.FR,
    },
  );

  try {
    const response = await apiCall<
      BackResponse<GetOrderByOrderReferenceAndOrderPriceResponse>
    >(url, { method: SUPPORTED_METHODS.GET });

    return response;
  } catch (exception) {
    return prepareResponseFailure(exception);
  }
}

export default getOrderByOrderReferenceAndOrderPrice;
